/**
 * The guided tour's stops, built from the person's OWN module tree and
 * rights: the modules the sidebar shows them, the pages inside each that
 * they may open, nothing they cannot reach. Pure, so it is tested without a
 * browser (`__tests__/tourSteps.test.ts`).
 *
 * A stop points at an element by `data-tour` (`[data-tour="<id>"]`), set on
 * the sidebar, the top bar and the Hub home. A stop whose element is not on
 * screen (the phone layout keeps the sidebar off-canvas) shows as a centred
 * card instead: the words still make sense without the spotlight.
 */
import type { ModuleDef, NavGroup, NavItem } from '@/shared/modules/registries';
import type { ModuleSlug } from '@/styles/tokens';

export interface TourStop {
  id: string;
  /** `data-tour` value of the element to light; null = a centred card. */
  target: string | null;
  /** Navigate here first (the tour's one visit inside a module). */
  path?: string;
  kicker: string;
  title: string;
  body: string;
  /** A short list under the body (a module's pages). */
  list?: string[];
}

export interface TourInput {
  /** The switcher modules this person can see, in Hub order. */
  modules: ModuleDef[];
  /** Hints per module slug (the Hub tiles' line). */
  hints: Partial<Record<string, string>>;
  /** Each module's pages, already filtered to what this person may open. */
  pages: Partial<Record<ModuleSlug, NavGroup[]>>;
  /** Where each module opens for this person (HR's Appraisals for non-admins). */
  moduleTo: (m: ModuleDef) => string;
  firstName: string;
  isAdmin: boolean;
  hasChecklist: boolean;
  /** The install is linked to Quotinc: a stop on the sidebar's Quotinc entry. */
  quotinc?: boolean;
}

/** Modules shown one by one before the rest are summed up in a sentence. */
export const MODULES_IN_DETAIL = 3;
const PAGES_LISTED = 4;

/** "A", "A and B", "A, B and C". */
export function humanList(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

function pageLabels(groups: NavGroup[] | undefined): string[] {
  const all: NavItem[] = (groups ?? []).flatMap((g) => g.items);
  return all.slice(0, PAGES_LISTED).map((i) => i.label);
}

export function buildTourSteps(input: TourInput): TourStop[] {
  const { modules, hints, pages, moduleTo, firstName, isAdmin, hasChecklist, quotinc = false } = input;
  // Preferences is everyone's and is introduced with the person, at the end.
  const work = modules.filter((m) => m.slug !== 'preferences');
  const stops: TourStop[] = [];

  stops.push({
    id: 'hello',
    target: null,
    kicker: 'tour · 2 minutes',
    title: firstName ? `A quick look around, ${firstName}` : 'A quick look around',
    body:
      work.length === 0
        ? 'Amassa shows each person the modules their rights open. Yours are not set yet: your administrator will add them. Here is where things live in the meantime.'
        : `You can open ${work.length} ${work.length === 1 ? 'module' : 'modules'}. This shows where each one lives. Leave any time with Esc, and replay it from Preferences ▸ Help.`,
  });

  stops.push({
    id: 'home',
    target: 'home',
    path: '/dashboard',
    kicker: 'the hub',
    title: 'Home is the mark',
    body: 'The Amassa mark at the top of the sidebar always brings you back to the Hub home, where every module you can open has its tile.',
  });

  if (work.length > 0) {
    stops.push({
      id: 'modules',
      target: 'modules',
      path: '/dashboard',
      kicker: 'your modules',
      title: 'Your modules',
      body: `On the Hub home the sidebar (the menu button on a phone) lists them: ${humanList(work.map((m) => m.label))}. What you see depends on your rights, so a colleague's list can differ.`,
    });
  }

  for (const m of work.slice(0, MODULES_IN_DETAIL)) {
    const list = pageLabels(pages[m.slug]);
    stops.push({
      id: `module:${m.slug}`,
      target: `module:${m.slug}`,
      path: '/dashboard',
      kicker: 'module',
      title: m.label,
      body: hints[m.slug] || m.blurb || `${m.label} opens from here.`,
      list: list.length ? list : undefined,
    });
  }
  if (work.length > MODULES_IN_DETAIL) {
    const rest = work.slice(MODULES_IN_DETAIL).map((m) => m.label);
    stops.push({
      id: 'more',
      target: 'modules',
      path: '/dashboard',
      kicker: 'and more',
      title: `${rest.length} more ${rest.length === 1 ? 'module' : 'modules'}`,
      body: `${humanList(rest)}. Each opens from the same list, or from its tile on the Hub home.`,
    });
  }

  // One visit inside a module: its pages in the sidebar.
  const first = work[0];
  if (first) {
    const groups = pages[first.slug] ?? [];
    const headings = groups.map((g) => g.heading).filter((h): h is string => !!h);
    stops.push({
      id: 'pages',
      target: 'pages',
      path: moduleTo(first),
      kicker: `inside ${first.label}`,
      title: 'Each module has its pages',
      body: headings.length
        ? `Inside a module the sidebar lists its pages under small labels (${humanList(headings.slice(0, 3))}). The page you are on is marked.`
        : 'Inside a module the sidebar lists its pages. The page you are on is marked.',
    });
    stops.push({
      id: 'switch',
      target: 'switcher',
      path: moduleTo(first),
      kicker: 'switching',
      title: 'Jump between modules',
      body: 'The name above the pages is a menu: pick another module, or the Hub, without going home first.',
    });
  }

  stops.push({
    id: 'search',
    target: 'search',
    path: '/dashboard',
    kicker: 'search',
    title: 'Find anything',
    body: 'The magnifier, or Cmd K (Ctrl K on Windows), searches bookings, guests, agents and pages from wherever you are.',
  });
  stops.push({
    id: 'bell',
    target: 'bell',
    path: '/dashboard',
    kicker: 'notices',
    title: 'Your notices',
    body: 'The bell collects what needs you: approvals, mentions, reminders from every module.',
  });
  if (quotinc) {
    stops.push({
      id: 'quotinc',
      target: 'quotinc',
      path: '/dashboard',
      kicker: 'the other app',
      title: 'Quotinc, one click away',
      body: 'Plans, billing and team access live in Quotinc. This entry opens it in a new tab, where you sign in as usual.',
    });
  }
  stops.push({
    id: 'me',
    target: 'me',
    path: '/dashboard',
    kicker: 'you',
    title: 'You, at the foot',
    body: 'Your name, the light or dark theme, and sign out. Preferences holds your password, Telegram and this tour.',
  });
  if (hasChecklist) {
    stops.push({
      id: 'checklist',
      target: 'checklist',
      path: '/dashboard',
      kicker: 'getting started',
      title: isAdmin ? 'Your setup list' : 'A few things to finish',
      body: isAdmin
        ? 'This list reads the workspace itself: each line ticks when the thing is really done. Hide it when you no longer need it.'
        : 'Each line ticks by itself when it is done. Hide it whenever you like.',
    });
  }
  stops.push({
    id: 'end',
    target: null,
    path: '/dashboard',
    kicker: 'that is it',
    title: 'You are ready',
    body: 'Replay this tour any time from Preferences ▸ Help. Your administrator can open more modules for you.',
  });
  return stops;
}
